<template>
  <div>
    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>Target retirement income <help-icon :message="TargetIncomeMessage"></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="TargetIncome" @update="setTargetIncome($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" filter="currencyDisplay" :property="TargetIncome" @change="setTargetIncome($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>Target retirement age <help-icon :message="RetirementAgeMessage"></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="RetirementAge" @update="setRetirementAge($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" :property="RetirementAge" @change="setRetirementAge($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row" v-if="IncludePartner">
      <div class="small-12 medium-4 columns">
        <label>Target partner retirement age</label>
      </div>

      <div class="medium-6 columns">
        <slider :property="RetirementAge_2" @update="setRetirementAge_2($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" :property="RetirementAge_2" @change="setRetirementAge_2($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>Target super run out age <help-icon message="We have used the default assumption set by ASIC for your target super run out age, but you should review to make sure it suits you." :is-error="RunOutAgeError" error-message="You’ve selected an age lower than your average life expectancy."></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="RunOutAge" @update="setRunOutAge($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" :property="RunOutAge" @change="setRunOutAge($event.target.value)"></custom-input>
      </div>
    </div>

    <div class="row">
      <div class="small-12 medium-4 columns">
        <label>One-off purchase amount <help-icon message="Examples of common one-off expenses include travel, a new boat or caravan, or renovating your home." :is-error="WithdrawalAtRetirementError" error-message="If you choose an amount that exceeds your balance at retirement it’ll be assumed you’re taking all your super as a one-off lump sum."></help-icon></label>
      </div>

      <div class="medium-6 columns">
        <slider :property="WithdrawalAtRetirement" @update="setWithdrawalAtRetirement($event.values[0])"></slider>
      </div>

      <div class="small-12 medium-2 columns">
        <custom-input type="number" filter="currencyDisplay" :property="WithdrawalAtRetirement" @change="setWithdrawalAtRetirement($event.target.value)"></custom-input>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { fnRun } from '@/store/helpers';

export default {
  props: {
    modifier: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState(['inputs', 'inferred', 'results']),
    TargetIncome() {
      return this.inferred[`TargetIncome${this.modifier}`];
    },
    IncludePartner() {
      return this.inputs.IncludePartner.value;
    },
    TargetIncomeMessage() {
      if (this.inputs.Salary.value > 0) {
        return 'When setting an income goal, consider the lifestyle you want to have in retirement. Include a combined income goal if you are including partner details. We have used the two-thirds of current income rule as a default.';
      }

      return 'When setting an income goal, consider the lifestyle you want to have in retirement. Include a combined income goal if you are including partner details. We have used the ASFA retirement standard for a comfortable lifestyle as a default.';
    },
    RetirementAge() {
      return this.inputs[`RetirementAge${this.modifier}`];
    },
    RetirementAge_2() {
      return this.inputs[`RetirementAge_2${this.modifier}`];
    },
    RetirementAgeMessage() {
      const minRetirementAge = this.fnRun(this.RetirementAge.range.min);
      return `There are rules around when you can access your super. Based on your age, you could access your super at age ${minRetirementAge}.`;
    },
    RunOutAge() {
      return this.inputs[`RunOutAge${this.modifier}`];
    },
    RunOutAgeError() {
      return this.RunOutAge.value < this.results.lifeExpectancy;
    },
    WithdrawalAtRetirementError() {
      return this.inputs[`WithdrawalAtRetirement${this.modifier}`].value >= this.results.deflatedCombinedBalanceAtRetirement;
    },
    WithdrawalAtRetirement() {
      return this.inputs[`WithdrawalAtRetirement${this.modifier}`];
    },
  },
  methods: {
    ...mapActions([
      'actionTargetIncome',
      'actionRetirementAge',
      'actionRetirementAge2',
      'actionRunOutAge',
      'actionWithdrawalAtRetirement',
    ]),
    setTargetIncome(value) {
      this.actionTargetIncome({ value, modifier: this.modifier });
    },
    setRetirementAge(value) {
      this.actionRetirementAge({ value, modifier: this.modifier });
    },
    setRetirementAge_2(value) {
      this.actionRetirementAge2({ value, modifier: this.modifier });
    },
    setRunOutAge(value) {
      this.actionRunOutAge({ value, modifier: this.modifier });
    },
    setWithdrawalAtRetirement(value) {
      this.actionWithdrawalAtRetirement({ value, modifier: this.modifier });
    },
  },
  created() {
    const { state } = this.$store;
    this.fnRun = fnRun.bind(state, state);
  },
};
</script>
